import * as React from 'react';
import notify from '../../../shared/feedback/notifications';
import * as actions from '../actions/PlanEditorOrdersActions';
import * as revisionTypes from '../../../components/common/planOrder/RevisionType';
import * as Features from '../../../shared/Features';
import FontFaceObserver from 'fontfaceobserver';
import {
    Button,
    ButtonGroup,
    ButtonToolbar,
    Col,
    ControlLabel,
    FormControl,
    FormGroup,
    HelpBlock,
    Modal,
    Row
} from 'react-bootstrap';
import { connect } from 'react-redux';
import { handleError } from '../../../shared/ErrorCatching';
import { Translate } from '../../../shared/LanguageService';
import { assign } from '../../../shared/Assign';
import { IFile, IUpdateSortModel, IUpdateStorey } from '../Entities';
import {
    IDelivery,
    IFileInfo,
    ISelectedImage,
    PlanOrderStoreyFileType
} from '../../../components/common/planOrder/Entities';
import DateTimeView, { LocalDateTime } from '../../../features/localDate/DateTimeView';
import { AnnotationTexts } from '../../planannotation/AnnotationTypes';
import {
    ICompareImagesStorey,
    TransformCompareStoreys
} from '../../../components/common/planOrder/comparing/CompareImages';
import CompareImageModal from '../../../components/common/planOrder/comparing/CompareImagesModal';
import PlanAnnotationTool from '../../planannotation/PlanAnnotationTool';
import FilePlacementsModal from '../../planannotation/FilePlacementsModal';
import ExternalFilesForm from '../../../components/common/planOrder/externalFiles/ExternalFilesForm';
import TemplatesForm from '../../../components/common/planOrder/TemplatesForm';
import FileTypeForm from './FileTypeForm';
import ImageModal from '../../../components/common/planOrder/ImageModal';
import CheckDelivery from '../../../components/common/planOrder/deliveryFiles/CheckDelivery';
import FormSelect from '../../../components/common/FormSelect';
import ConfirmationModal from '../../../components/common/ConfirmationModal';
import Dropzone from '../../../components/common/Dropzone';
import StoreySelect from '../../../components/common/planOrder/StoreySelect';
import StoreySort from '../../../components/common/planOrder/StoreySort';
import FormValidationService from '../../../shared/form/FormValidationService';
import StoreyExternalFilesModal from '../../../components/common/planOrder/externalFiles/StoreyExternalFilesModal';

interface IPlanOrder {
    id: number;
    deadline: LocalDateTime;
    status: string;
    information: string;
    extraInformation: string;
    orderNumber: number;
    orderCollectionNumber: number;
    address: string;

    plan2D: boolean;
    plan3D: boolean;
    isExpress: boolean;

    products: string[];
    templates: ITemplate[];
    stories: IStorey[];
    externalDeliveryFiles: IExternalPlanOrderFile[];
    internalExternalFiles: IExternalPlanOrderFile[];

    annotationTextInfo: IAnnotationTextInfo;

    delivery: IDelivery;
    shouldGenerate: boolean;
    isReturned: boolean;
    planEditorId?: number;
}

interface IAnnotationTextInfo {
    commonTexts: string[];
    font: string;
    upperCase: boolean;
}

interface IExternalPlanOrderFile extends IFileInfo {
    planOrderId: number;
}

interface ITemplate {
    fileReference: string;
    name: string;
}

interface ISelect {
    id: number;
    name: string;
}

interface IStorey {
    id: number;

    storeyId: number;
    customStoreyName: string;
    availableStories: ISelect[];

    file: IFile;

    file2D: IFile;
    file3D: IFile;

    externalFiles: IExternalStoreyFile[];
}

interface IExternalStoreyFile {
    planOrderStoreyId: number;
    fileInfoId: number;
    filename: string;
}

interface ISelectPlanEditor {
    id: number;
    name: string;
}

interface IActions {
    editPlanOrder: (planOrderId: number) => Promise<any>;
    addPlanOrderExternalFile: (
        id: number,
        planOrderId: number,
        fileInfoId: number,
        filename: string,
        fileReference: string,
        fileType: string,
        thumbnailReference: string,
        oldFileId: number,
        category: number
    ) => Promise<any>;
    addPlanOrderStoreyExternalFile: (
        planOrderStoreyId: number,
        fileInfoId: number,
        filename: string,
        fileReference: string,
        fileType: string,
        thumbnailReference: string,
        oldFileInfoId: number,
        oldFileReference: string
    ) => Promise<any>;
    deletePlanOrderExternalFile: (planOrderId: number, fileInfoId: number) => Promise<any>;
    deletePlanOrderStoreyExternalFile: (
        planOrderId: number,
        planOrderStoreyId: number,
        fileInfoId: number
    ) => Promise<any>;
    addPlanOrderStorey2DFile: (planOrderStoreyId: number, file: IFile) => Promise<any>;
    addPlanOrderStorey3DFile: (planOrderStoreyId: number, file: IFile) => Promise<any>;
    approvePlanOrder: (planOrderId: number) => Promise<any>;
    generateFiles: (planOrderId: number) => Promise<any>;
    downloadFiles: (planOrderId: number) => Promise<any>;
    editAnnotation: (id: number) => Promise<any>;
    saveAnnotationTexts: (planOrderFileRevisionId: number, data: string) => Promise<any>;
    updatePlacements: (
        planOrderId: number,
        files: any[],
        planOrderStoreyFileId: number
    ) => Promise<any>;
    updatePlanEditor: (
        planOrderId: number,
        planEditorId: number,
        dontRemoveFromList: boolean
    ) => Promise<any>;
    createEmptyStorey: (planOrderId: number) => Promise<any>;
    createStorey: (storey: any) => Promise<any>;
    updateSort: (planOrderId: number, storey: IUpdateSortModel) => Promise<any>;
    deleteStorey: (planOrder: number, storeyId: number) => Promise<any>;
    updateStorey: (
        planOrderId: number,
        planOrderStoreyId: number,
        data: IUpdateStorey
    ) => Promise<any>;
    deleteDeliveryFile: (reference: string) => Promise<any>;
    getCompareData: (planOrderId: number) => Promise<any>;
}

interface IProps {
    planOrder: IPlanOrder;
    planEditors: ISelectPlanEditor[];
    planOrderId: number;
    onClose: () => void;
    actions: IActions;
    annotation: any;
    user: any;
    compareStoreys: ICompareImagesStorey[];
}

interface IState {
    stories: { [id: number]: string };
    storiesUploads: {
        [id: number]: { external: boolean; file2D: boolean; file3D: boolean };
    };
    selectedPlanOrderExternalFile: string;
    planOrderUpload: boolean;
    uploadToCustomer: boolean;
    showAnnotationModal: boolean;
    isGenerating: boolean;
    showImageModal: boolean;
    selectedImage: ISelectedImage;
    deliveryFileWasSelected: boolean;
    isCheckTabDisabled: boolean;
    showFilePlacementsModal: boolean;
    placementModalPlanOrderStoreyFileId: number;
    planEditorId: number;
    showUpdatePlanEditorConfirmModal: boolean;
    isLoading: boolean;
    uploadNewStoreys: boolean;
    errors: any;
    showDeleteStoreyConfirmation: boolean;
    confirmationDoAction?: () => any;
    showStoreyExternalFilesModal: boolean;
    selectedExternalStoreyIndex: number;
    selectedExternalStoreyFile: string;
    showCompareModal: boolean;
    storiesForCompare: ICompareImagesStorey[];
    selectedStoreyIdForCompare: number;
    fontSizeRatioForTextEdit?: number | null;
    selectedStoreyFileType: number;
}

class PlanEditorOrderModal extends React.Component<IProps, IState> {
    constructor(props: IProps, context: any) {
        super(props, context);

        this.state = {
            stories: {},
            storiesUploads: {},
            planOrderUpload: false,
            uploadToCustomer: false,
            selectedPlanOrderExternalFile: '',
            showAnnotationModal: false,
            isGenerating: false,
            showImageModal: false,
            selectedImage: null,
            deliveryFileWasSelected: false,
            isCheckTabDisabled: false,
            showFilePlacementsModal: false,
            placementModalPlanOrderStoreyFileId: null,
            planEditorId: null,
            showUpdatePlanEditorConfirmModal: false,
            isLoading: false,
            uploadNewStoreys: false,
            errors: {},
            showDeleteStoreyConfirmation: false,
            showStoreyExternalFilesModal: false,
            selectedExternalStoreyIndex: null,
            selectedExternalStoreyFile: '',
            showCompareModal: false,
            storiesForCompare: [],
            selectedStoreyIdForCompare: null,
            selectedStoreyFileType: 0
        };

        this.onPlanOrderExternalFilesUploadCompleted =
            this.onPlanOrderExternalFilesUploadCompleted.bind(this);
        this.uploadPlanOrderExternalFiles = this.uploadPlanOrderExternalFiles.bind(this);
        this.onStoreyExternalFileUploadCompleted =
            this.onStoreyExternalFileUploadCompleted.bind(this);
        this.approvePlanOrder = this.approvePlanOrder.bind(this);
        this.generateFiles = this.generateFiles.bind(this);
        this.downloadFiles = this.downloadFiles.bind(this);
        this.saveAnnotation = this.saveAnnotation.bind(this);
        this.onSelectImage = this.onSelectImage.bind(this);
        this.isFileAnnotation = this.isFileAnnotation.bind(this);
        this.canAnnotatePlacement = this.canAnnotatePlacement.bind(this);
        this.showFilePlacementsModal = this.showFilePlacementsModal.bind(this);
        this.savePlacements = this.savePlacements.bind(this);
        this.updatePlanEditor = this.updatePlanEditor.bind(this);
        this.updatePlanEditorConfirmation = this.updatePlanEditorConfirmation.bind(this);
        this.onEditTexts = this.onEditTexts.bind(this);
        this.deletePlanOrderStoreyExternalFile = this.deletePlanOrderStoreyExternalFile.bind(this);
        this.onPlanOrderExternalFileUploadCompleted =
            this.onPlanOrderExternalFileUploadCompleted.bind(this);
        this.deletePlanOrderExternalFile = this.deletePlanOrderExternalFile.bind(this);
        this.onUploadNewStoreysCompleted = this.onUploadNewStoreysCompleted.bind(this);
        this.onNewStoreyUploadCompleted = this.onNewStoreyUploadCompleted.bind(this);
        this.createEmptyStorey = this.createEmptyStorey.bind(this);
        this.uploadNewStoreys = this.uploadNewStoreys.bind(this);
        this.updateSort = this.updateSort.bind(this);
        this.updateStorey = this.updateStorey.bind(this);
        this.onChangeStorey = this.onChangeStorey.bind(this);
        this.deleteStorey = this.deleteStorey.bind(this);
        this.openStoreyExternalFileModal = this.openStoreyExternalFileModal.bind(this);
        this.uploadStoreyFile = this.uploadStoreyFile.bind(this);
        this.onStoreyExternalFilesUploadCompleted =
            this.onStoreyExternalFilesUploadCompleted.bind(this);
        this.shouldGenerate = this.shouldGenerate.bind(this);
        this.getExternalFiles = this.getExternalFiles.bind(this);
        this.onCompareFilterChanged = this.onCompareFilterChanged.bind(this);
        this.deleteDeliveryFile = this.deleteDeliveryFile.bind(this);
    }

    componentDidMount() {
        this.setState({ isLoading: true } as IState);
        this.props.actions.editPlanOrder(this.props.planOrderId).then(() => {
            this.disableCheckTab();

            if (!this.state.planEditorId && this.props.planOrder.planEditorId) {
                this.setState({
                    planEditorId: this.props.planOrder.planEditorId
                } as IState);
            }

            this.setState({ isLoading: false } as IState);
        });
    }

    UNSAFE_componentWillReceiveProps(nextProps: any) {
        if (nextProps.planOrder) {
            if (nextProps.planOrder.annotationTextInfo) {
                new FontFaceObserver(nextProps.planOrder.annotationTextInfo.font).load();
            }

            this.disableCheckTab(nextProps.planOrder);
        }
    }

    shouldComponentUpdate(nextProps: IProps, nextState: IState) {
        if (
            JSON.stringify(this.props) !== JSON.stringify(nextProps) ||
            JSON.stringify(this.state) !== JSON.stringify(nextState)
        ) {
            return true;
        }

        return false;
    }

    render() {
        const { apiUrl } = window.appConfig;

        return (
            <Modal show={true} onHide={this.props.onClose} bsSize="large">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {Translate('planeditor/orders/edit/title') +
                            this.props.planOrder.orderCollectionNumber +
                            ':' +
                            this.props.planOrder.orderNumber}
                        {this.props.planOrder.address ? ' - ' + this.props.planOrder.address : ''}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.state.isLoading && <div className="load-spin-dark"></div>}
                    {!this.state.isLoading && (
                        <div>
                            <fieldset>
                                <legend>
                                    {Translate('planeditor/orders/edit/order-info-status/title')}
                                </legend>
                                <Row>
                                    <Col sm={4}>
                                        <FormGroup>
                                            <ControlLabel>
                                                {Translate('planeditor/orders/edit/deadline')}
                                            </ControlLabel>
                                            <FormControl.Static>
                                                <DateTimeView
                                                    dateTime={this.props.planOrder.deadline}
                                                    format="L LT"
                                                />
                                            </FormControl.Static>
                                        </FormGroup>
                                        <FormGroup>
                                            <ControlLabel>
                                                {Translate('planeditor/orders/edit/status')}
                                            </ControlLabel>
                                            <FormControl.Static>
                                                {Translate(this.props.planOrder.status)}
                                            </FormControl.Static>
                                        </FormGroup>
                                        {this.props.planOrder.isExpress && (
                                            <FormGroup>
                                                <ControlLabel>
                                                    {Translate('planeditor/orders/edit/is-express')}
                                                </ControlLabel>
                                                <FormControl.Static>
                                                    <i className="material-icons">done</i>
                                                </FormControl.Static>
                                            </FormGroup>
                                        )}
                                        <FormGroup>
                                            <ControlLabel>
                                                {Translate('planeditor/orders/edit/products')}
                                            </ControlLabel>
                                            {this.props.planOrder.products &&
                                                this.props.planOrder.products.map((x, index) => (
                                                    <FormControl.Static key={'product-' + index}>
                                                        {x}
                                                    </FormControl.Static>
                                                ))}
                                        </FormGroup>
                                    </Col>
                                    <Col sm={4}>
                                        <FormGroup>
                                            <ControlLabel>
                                                {Translate(
                                                    'planeditor/orders/edit/extra-information'
                                                )}
                                            </ControlLabel>
                                            <FormControl.Static>
                                                {this.props.planOrder.extraInformation}
                                            </FormControl.Static>
                                        </FormGroup>
                                        <FormGroup>
                                            <ControlLabel>
                                                {Translate('planeditor/orders/edit/information')}
                                            </ControlLabel>
                                            <FormControl.Static>
                                                {this.props.planOrder.information}
                                            </FormControl.Static>
                                        </FormGroup>

                                        <FormGroup>
                                            <ControlLabel>
                                                {Translate('planeditor/orders/edit/plan-editor')}
                                            </ControlLabel>
                                            <Row>
                                                <Col sm={8}>
                                                    <FormSelect
                                                        name="planEditorId"
                                                        value={
                                                            this.state.planEditorId
                                                                ? this.state.planEditorId.toString()
                                                                : ''
                                                        }
                                                        onChange={(e: any) =>
                                                            this.setState({
                                                                planEditorId: e.target.value
                                                            } as IState)
                                                        }
                                                    >
                                                        <option value="">
                                                            {Translate(
                                                                'planeditor/orders/edit/plan-editor/default-value'
                                                            )}
                                                        </option>
                                                        {this.props.planEditors.map(
                                                            (
                                                                t: ISelectPlanEditor,
                                                                index: number
                                                            ) => (
                                                                <option
                                                                    key={index}
                                                                    value={t.id.toString()}
                                                                >
                                                                    {t.name}
                                                                </option>
                                                            )
                                                        )}
                                                    </FormSelect>
                                                </Col>
                                                <Col sm={4}>
                                                    <Button
                                                        bsStyle="success"
                                                        onClick={this.updatePlanEditorConfirmation}
                                                    >
                                                        {Translate(
                                                            'planeditor/orders/edit/plan-editor/update'
                                                        )}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </FormGroup>
                                    </Col>
                                    <Col sm={4}>
                                        <TemplatesForm templates={this.props.planOrder.templates} />
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xs={12}>
                                        <ButtonGroup>
                                            <Button onClick={this.downloadFiles}>
                                                {Translate(
                                                    'planeditor/orders/edit/download-files-button'
                                                )}
                                            </Button>
                                            {this.props.planOrder.planEditorId && (
                                                <span>
                                                    {this.props.planOrder.shouldGenerate && (
                                                        <Button
                                                            bsStyle="info"
                                                            onClick={this.generateFiles}
                                                            disabled={this.state.isGenerating}
                                                        >
                                                            {Translate(
                                                                'plan-order/delivery/generate-files-button'
                                                            )}
                                                            {this.state.isGenerating && (
                                                                <i className="load-spin-dark"></i>
                                                            )}
                                                        </Button>
                                                    )}

                                                    <Button
                                                        onClick={this.approvePlanOrder}
                                                        bsStyle="success"
                                                    >
                                                        {Translate(
                                                            'plan-order/delivery/approve-button'
                                                        )}
                                                    </Button>
                                                </span>
                                            )}
                                        </ButtonGroup>
                                    </Col>
                                </Row>
                            </fieldset>
                            <div>
                                {this.props.planOrder.stories &&
                                    this.props.planOrder.stories.map((storey, index) => (
                                        <div
                                            key={'storey-' + storey.id}
                                            className="plan-sketch-storeys plan-user"
                                        >
                                            <div className="storey-tools">
                                                <StoreySort
                                                    index={index}
                                                    storiesLength={
                                                        this.props.planOrder.stories.length
                                                    }
                                                    moveUp={() => this.updateSort(storey.id, 'up')}
                                                    moveDown={() =>
                                                        this.updateSort(storey.id, 'down')
                                                    }
                                                    disabled={!this.props.planOrder.planEditorId}
                                                />
                                                <FormGroup
                                                    className="required storey-select"
                                                    validationState={FormValidationService.getValidationState(
                                                        this.state.errors[`stories[${index}]`]
                                                    )}
                                                >
                                                    <StoreySelect
                                                        key={'storey-select-' + storey.id}
                                                        value={{
                                                            id: storey.storeyId,
                                                            name: storey.customStoreyName
                                                                ? storey.customStoreyName
                                                                : Translate(
                                                                      storey.availableStories &&
                                                                          storey.availableStories.find(
                                                                              (x) =>
                                                                                  x.id ===
                                                                                  storey.storeyId
                                                                          )
                                                                          ? storey.availableStories.find(
                                                                                (x) =>
                                                                                    x.id ===
                                                                                    storey.storeyId
                                                                            ).name
                                                                          : ''
                                                                  )
                                                        }}
                                                        options={
                                                            storey.availableStories
                                                                ? this.props.planOrder.planEditorId
                                                                    ? storey.availableStories
                                                                    : storey.availableStories.filter(
                                                                          (s: ISelect) =>
                                                                              s.id ===
                                                                              storey.storeyId
                                                                      )
                                                                : []
                                                        }
                                                        onChange={
                                                            this.props.planOrder.planEditorId
                                                                ? (e: any) =>
                                                                      this.onChangeStorey(e, index)
                                                                : undefined
                                                        }
                                                    />
                                                    <HelpBlock>
                                                        {this.state.errors &&
                                                            this.state.errors[`stories`] &&
                                                            this.state.errors[`stories[${index}]`]}
                                                    </HelpBlock>
                                                </FormGroup>
                                                {this.props.planOrder.planEditorId && (
                                                    <ButtonToolbar className="pull-right">
                                                        <Button
                                                            bsStyle="danger"
                                                            onClick={() =>
                                                                this.deleteStorey(storey.id)
                                                            }
                                                        >
                                                            <i className="material-icons md-18 delete">
                                                                delete_forever
                                                            </i>{' '}
                                                            {Translate(
                                                                'order/plan/storey/delete-button'
                                                            )}
                                                        </Button>
                                                        <Button
                                                            bsStyle="success"
                                                            onClick={() =>
                                                                this.openStoreyExternalFileModal(
                                                                    index
                                                                )
                                                            }
                                                        >
                                                            {Translate(
                                                                'order/plan/storey/external-files-button'
                                                            )}{' '}
                                                            (
                                                            {storey.externalFiles
                                                                ? storey.externalFiles.length
                                                                : 0}
                                                            )
                                                        </Button>
                                                    </ButtonToolbar>
                                                )}
                                            </div>
                                            <Row>
                                                <Col sm={5}>
                                                    <fieldset className="planeditor-fieldset">
                                                        <legend>
                                                            {Translate(
                                                                'planeditor/orders/edit/workflow/from-customer'
                                                            )}
                                                        </legend>
                                                        {storey.file && (
                                                            <img
                                                                onClick={() =>
                                                                    this.onSelectImage(
                                                                        storey.file,
                                                                        false
                                                                    )
                                                                }
                                                                src={`${apiUrl}/file/${storey.file.thumbnailReference}`}
                                                            />
                                                        )}
                                                    </fieldset>
                                                </Col>
                                                {this.props.planOrder.plan2D && (
                                                    <FileTypeForm
                                                        file={storey.file2D}
                                                        dropzoneId={'storey-2d-' + index}
                                                        actionUrl={`${apiUrl}/views/planeditor/orders/${this.props.planOrder.id}/file/storey/${storey.id}/2d`}
                                                        upload={
                                                            this.state.storiesUploads[storey.id]
                                                                ? this.state.storiesUploads[
                                                                      storey.id
                                                                  ].file2D
                                                                : false
                                                        }
                                                        onUploadCompleted={() =>
                                                            this.onStorey2DFilesUploadCompleted(
                                                                storey.id
                                                            )
                                                        }
                                                        onFileUploadCompleted={(response: any) =>
                                                            this.onStorey2DFileUploadCompleted(
                                                                response,
                                                                storey.id,
                                                                storey.file2D
                                                            )
                                                        }
                                                        onUpload={() =>
                                                            this.uploadStorey2DFile(storey.id)
                                                        }
                                                        title={Translate(
                                                            'plan-order/delivery/2d-files/title'
                                                        )}
                                                        onEditTexts={() =>
                                                            this.onEditTexts(storey.file2D.id)
                                                        }
                                                        onClick={() =>
                                                            this.onSelectImage(storey.file2D, false)
                                                        }
                                                        shouldUseEditTexts={
                                                            this.props.planOrder.shouldGenerate
                                                        }
                                                        isAnnotation={this.isFileAnnotation(
                                                            storey.file2D
                                                        )}
                                                        onCompare={() =>
                                                            this.onSelectCompareImage(
                                                                storey.id,
                                                                PlanOrderStoreyFileType.sketchUp2D,
                                                                false
                                                            )
                                                        }
                                                        readonly={
                                                            !this.props.planOrder.planEditorId
                                                        }
                                                    />
                                                )}
                                                {this.props.planOrder.plan3D && (
                                                    <FileTypeForm
                                                        file={storey.file3D}
                                                        dropzoneId={'storey-3d-' + index}
                                                        actionUrl={`${apiUrl}/views/planeditor/orders/${this.props.planOrder.id}/file/storey/${storey.id}/3d`}
                                                        upload={
                                                            this.state.storiesUploads[storey.id]
                                                                ? this.state.storiesUploads[
                                                                      storey.id
                                                                  ].file3D
                                                                : false
                                                        }
                                                        onUploadCompleted={() =>
                                                            this.onStorey3DFilesUploadCompleted(
                                                                storey.id
                                                            )
                                                        }
                                                        onFileUploadCompleted={(response: any) =>
                                                            this.onStorey3DFileUploadCompleted(
                                                                response,
                                                                storey.id,
                                                                storey.file3D
                                                            )
                                                        }
                                                        onUpload={() =>
                                                            this.uploadStorey3DFile(storey.id)
                                                        }
                                                        title={Translate(
                                                            'plan-order/delivery/3d-files/title'
                                                        )}
                                                        onEditTexts={() =>
                                                            this.onEditTexts(storey.file3D.id)
                                                        }
                                                        onClick={() =>
                                                            this.onSelectImage(storey.file3D, false)
                                                        }
                                                        shouldUseEditTexts={
                                                            this.props.planOrder.shouldGenerate
                                                        }
                                                        isAnnotation={this.isFileAnnotation(
                                                            storey.file3D
                                                        )}
                                                        onCompare={() =>
                                                            this.onSelectCompareImage(
                                                                storey.id,
                                                                PlanOrderStoreyFileType.sketchUp3D,
                                                                false
                                                            )
                                                        }
                                                        readonly={
                                                            !this.props.planOrder.planEditorId
                                                        }
                                                    />
                                                )}
                                            </Row>
                                        </div>
                                    ))}
                            </div>
                            <Row>
                                <Col md={12}>
                                    <CheckDelivery
                                        stories={this.props.planOrder.stories}
                                        delivery={this.props.planOrder.delivery}
                                        shouldGenerate={this.shouldGenerate}
                                        isGenerating={this.state.isGenerating}
                                        generate={this.generateFiles}
                                        approve={this.approvePlanOrder}
                                        onSelectImage={this.onSelectImage}
                                        isLocked={true}
                                        user={this.props.user}
                                        deleteDeliveryFile={this.deleteDeliveryFile}
                                        inActivateExternalFile={null}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col md={5}>
                                    <fieldset className="planeditor-fieldset">
                                        <legend>{Translate('order/plan/new-storey-title')}</legend>
                                        <div className="button-bar-below">
                                            <div className="content-row">
                                                {this.props.planOrder.planEditorId && (
                                                    <Dropzone
                                                        actionUrl={`${apiUrl}/views/planorder/${this.props.planOrder.id}/storey`}
                                                        upload={this.state.uploadNewStoreys}
                                                        onUploadCompleted={
                                                            this.onUploadNewStoreysCompleted
                                                        }
                                                        onFileUploadCompleted={
                                                            this.onNewStoreyUploadCompleted
                                                        }
                                                        uploadMultiple={true}
                                                        removeFileAfterSuccess={true}
                                                        acceptedFiles={
                                                            'application/pdf,image/jpeg,image/png,image/gif'
                                                        }
                                                    />
                                                )}
                                            </div>
                                            <div className="button-row">
                                                {this.props.planOrder.planEditorId && (
                                                    <Button
                                                        bsSize="sm"
                                                        bsStyle="info"
                                                        className="pull-right"
                                                        onClick={this.uploadNewStoreys}
                                                    >
                                                        <i className="material-icons">
                                                            cloud_upload
                                                        </i>
                                                        {Translate('order/plan/upload-button')}
                                                    </Button>
                                                )}
                                                <Button
                                                    bsSize="sm"
                                                    bsStyle="success"
                                                    className="pull-right"
                                                    onClick={this.createEmptyStorey}
                                                >
                                                    <i className="material-icons">add</i>
                                                    {Translate(
                                                        'order/plan/create-empty-storey-button'
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </fieldset>
                                </Col>
                                <Col md={7}>
                                    <ExternalFilesForm
                                        dropzoneId={'dropzone-plan-order'}
                                        actionUrl={`${apiUrl}/views/planeditor/orders/${this.props.planOrderId}/file`}
                                        actionUrlToCustomer={null}
                                        upload={this.state.planOrderUpload}
                                        uploadToCustomer={null}
                                        onUploadCompleted={
                                            this.onPlanOrderExternalFilesUploadCompleted
                                        }
                                        onUpload={this.uploadPlanOrderExternalFiles}
                                        onUploadToCustomer={null}
                                        title={Translate(
                                            'planeditor/orders/edit/plan-order/external-files/title'
                                        )}
                                        selectedFile={this.state.selectedPlanOrderExternalFile}
                                        onSelectedFileChanged={(e: any) =>
                                            this.uploadedPlanOrderExternalFileChanged(e)
                                        }
                                        files={this.getExternalFiles()}
                                        onDeleteFile={() =>
                                            this.deletePlanOrderExternalFile(
                                                this.getExternalFiles()[
                                                    this.state.selectedPlanOrderExternalFile
                                                ]
                                            )
                                        }
                                        onFileUploadCompleted={
                                            this.onPlanOrderExternalFileUploadCompleted
                                        }
                                        readOnly={!this.props.planOrder.planEditorId}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={this.props.onClose}>
                        {Translate('planeditor/orders/edit/close')}
                    </Button>
                </Modal.Footer>

                {this.state.showAnnotationModal && this.props.annotation && (
                    <PlanAnnotationTool
                        annotation={this.props.annotation}
                        save={this.saveAnnotation}
                        storeys={this.props.planOrder.stories}
                        close={() =>
                            this.setState({
                                showAnnotationModal: false,
                                fontSizeRatioForTextEdit: null
                            } as IState)
                        }
                        commonTexts={this.props.planOrder.annotationTextInfo.commonTexts}
                        fontFamily={this.props.planOrder.annotationTextInfo.font}
                        upperCase={this.props.planOrder.annotationTextInfo.upperCase}
                        type={AnnotationTexts}
                        user={this.props.user}
                        address={this.props.planOrder.address}
                        changeFile={this.onEditTexts}
                        showFilePlacementsModal={
                            this.canAnnotatePlacement()
                                ? (planOrderStoreyFileId: number) =>
                                      this.showFilePlacementsModal(planOrderStoreyFileId)
                                : null
                        }
                        fontSizeRatio={this.state.fontSizeRatioForTextEdit}
                    />
                )}
                {this.state.showFilePlacementsModal && (
                    <FilePlacementsModal
                        planOrderId={this.props.planOrder.id}
                        images2D={this.props.planOrder.stories
                            .filter((x) => x.file2D)
                            .map((x) => x.file2D)}
                        images3D={this.props.planOrder.stories
                            .filter((x) => x.file3D)
                            .map((x) => x.file3D)}
                        closeModal={() =>
                            this.setState({
                                showFilePlacementsModal: false
                            } as IState)
                        }
                        onSave={(files: any[]) =>
                            this.savePlacements(
                                files,
                                this.state.placementModalPlanOrderStoreyFileId
                            )
                        }
                    />
                )}
                {this.state.showImageModal && this.state.selectedImage && (
                    <ImageModal
                        filename={this.state.selectedImage.filename}
                        fileReference={this.state.selectedImage.fileReference}
                        onClose={() => {
                            this.setState({
                                showImageModal: false,
                                selectedImage: null
                            } as IState);
                        }}
                    />
                )}
                {this.state.showImageModal &&
                    this.state.selectedImage &&
                    this.state.deliveryFileWasSelected && (
                        <ImageModal
                            filename={this.state.selectedImage.filename}
                            fileReference={this.state.selectedImage.fileReference}
                            onClose={() => {
                                this.setState({
                                    showImageModal: false,
                                    selectedImage: null,
                                    deliveryFileWasSelected: false
                                } as IState);
                            }}
                        />
                    )}
                {this.state.showUpdatePlanEditorConfirmModal && (
                    <ConfirmationModal
                        title={Translate(
                            'planeditor/orders/edit/plan-editor/confirmation/modal/title'
                        )}
                        text={Translate(
                            'planeditor/orders/edit/plan-editor/confirmation/modal/text'
                        )}
                        doButtonLabel={Translate(
                            'planeditor/orders/edit/plan-editor/confirmation/modal/do-button'
                        )}
                        cancelButtonLabel={Translate(
                            'planeditor/orders/edit/plan-editor/confirmation/modal/cancel-button'
                        )}
                        doButtonStyle="danger"
                        do={this.updatePlanEditor}
                        closeModal={() =>
                            this.setState({
                                showUpdatePlanEditorConfirmModal: false
                            } as IState)
                        }
                    />
                )}
                {this.state.showDeleteStoreyConfirmation && (
                    <ConfirmationModal
                        title={Translate('order/plan/delete-storey/confirmation/title')}
                        text={Translate('order/plan/delete-storey/confirmation/text')}
                        doButtonLabel={Translate('order/plan/delete-storey/confirmation/do-button')}
                        cancelButtonLabel={Translate(
                            'order/plan/delete-storey/confirmation/cancel-button'
                        )}
                        doButtonStyle="danger"
                        do={this.state.confirmationDoAction}
                        closeModal={() =>
                            this.setState({
                                showDeleteStoreyConfirmation: false,
                                confirmationDoAction: null
                            } as IState)
                        }
                    />
                )}
                {this.state.showStoreyExternalFilesModal && (
                    <StoreyExternalFilesModal
                        title={Translate('order/plan/storey/external-files-button')}
                        selectedFiles={this.state.selectedExternalStoreyFile}
                        externalFiles={
                            this.props.planOrder.stories[this.state.selectedExternalStoreyIndex]
                                .externalFiles
                        }
                        dropzoneId={'dropzone-storey-' + this.state.selectedExternalStoreyIndex}
                        actionUrl={`${apiUrl}/views/planeditor/orders/${
                            this.props.planOrder.id
                        }/file/storey/${
                            this.props.planOrder.stories[this.state.selectedExternalStoreyIndex].id
                        }`}
                        upload={
                            this.state.storiesUploads[
                                this.props.planOrder.stories[this.state.selectedExternalStoreyIndex]
                                    .id
                            ]
                                ? this.state.storiesUploads[
                                      this.props.planOrder.stories[
                                          this.state.selectedExternalStoreyIndex
                                      ].id
                                  ].external
                                : false
                        }
                        uploadedFilesChanged={(e: any) => this.uploadedFilesChanged(e)}
                        onDeleteFile={this.deletePlanOrderStoreyExternalFile}
                        onUploadCompleted={this.onStoreyExternalFilesUploadCompleted}
                        onFileUploadCompleted={this.onStoreyExternalFileUploadCompleted}
                        onUpload={() =>
                            this.uploadStoreyFile(
                                this.props.planOrder.stories[this.state.selectedExternalStoreyIndex]
                                    .id,
                                'external'
                            )
                        }
                        closeModal={() =>
                            this.setState({
                                showStoreyExternalFilesModal: false
                            } as IState)
                        }
                    />
                )}
                {this.state.showCompareModal &&
                    this.state.storiesForCompare &&
                    this.state.storiesForCompare.length > 0 && (
                        <CompareImageModal
                            storeys={this.state.storiesForCompare}
                            selectedStoreyId={this.state.selectedStoreyIdForCompare}
                            selectedStoreyFileType={this.state.selectedStoreyFileType}
                            onClose={() => {
                                this.setState({
                                    showCompareModal: false
                                } as IState);
                            }}
                            onFilterChanged={this.onCompareFilterChanged}
                            address={this.props.planOrder.address}
                        />
                    )}
            </Modal>
        );
    }

    onCompareFilterChanged(showEverything: boolean) {
        this.onSelectCompareImage(
            this.state.selectedStoreyIdForCompare,
            this.state.selectedStoreyFileType,
            showEverything
        );
    }

    onSelectCompareImage(storeyId: number, storeyFileType: number, showEverything: boolean) {
        this.props.actions.getCompareData(this.props.planOrder.id).then(() => {
            let storeys = TransformCompareStoreys(this.props.compareStoreys, showEverything);

            this.setState({
                showCompareModal: true,
                storiesForCompare: storeys,
                selectedStoreyIdForCompare: storeyId,
                selectedStoreyFileType: storeyFileType
            } as IState);
        });
    }

    disableCheckTab(planOrder?: IPlanOrder): void {
        if (!planOrder) {
            planOrder = this.props.planOrder;
        }

        if (!planOrder.stories) {
            this.setState({ isCheckTabDisabled: false } as IState);
            return;
        }

        let disabled = planOrder.stories.some((x) => {
            if (planOrder.plan2D && !x.file2D) {
                return true;
            }

            if (planOrder.plan3D && !x.file3D) {
                return true;
            }
        });

        this.setState({ isCheckTabDisabled: disabled } as IState);
    }

    onEditTexts(id: number) {
        this.props.actions.editAnnotation(id).then((result) => {
            this.setState(
                {
                    showAnnotationModal: true,
                    fontSizeRatioForTextEdit: null
                } as IState,
                () => {
                    if (result.enforceFilePlacement) {
                        this.showFilePlacementsModal(id);
                    }
                }
            );
        });
    }

    saveAnnotation(id: number, data: string) {
        return this.props.actions
            .saveAnnotationTexts(id, data)
            .catch((error: any) => handleError(error));
    }

    uploadedFilesChanged(e: any) {
        this.setState({ selectedExternalStoreyFile: e.target.value } as IState);
    }

    uploadStoreyFiles(storeyId: number) {
        let storiesUploads = assign({}, this.state.storiesUploads);

        if (!storiesUploads[storeyId]) {
            storiesUploads[storeyId] = {};
        }

        storiesUploads[storeyId].external = true;
        this.setState({ storiesUploads: storiesUploads } as IState);
    }

    onStoreyExternalFilesUploadCompleted() {
        let storiesUploads = assign({}, this.state.storiesUploads);

        let index = this.state.selectedExternalStoreyIndex;
        let storey = this.props.planOrder.stories[index];
        storiesUploads[storey.id].external = false;

        this.setState({ storiesUploads: storiesUploads } as IState);
    }

    onStoreyExternalFileUploadCompleted(response: any) {
        response = response.xhr.response;

        if (response) {
            response = JSON.parse(response);
            this.props.actions.addPlanOrderStoreyExternalFile(
                response.planOrderStoreyId,
                response.fileInfoId,
                response.filename,
                response.fileReference,
                response.fileType,
                response.thumbnailReference,
                response.oldFileInfoId,
                response.oldFileReference
            );
        }
    }

    deletePlanOrderStoreyExternalFile() {
        let file =
            this.props.planOrder.stories[this.state.selectedExternalStoreyIndex].externalFiles[
                this.state.selectedExternalStoreyFile
            ];
        this.props.actions.deletePlanOrderStoreyExternalFile(
            this.props.planOrder.id,
            file.planOrderStoreyId,
            file.fileInfoId
        );
    }

    uploadedPlanOrderExternalFileChanged(e: any) {
        let selected = assign({}, this.state.selectedPlanOrderExternalFile);
        selected = e.target.value;

        this.setState({ selectedPlanOrderExternalFile: selected } as IState);
    }

    uploadPlanOrderExternalFiles() {
        this.setState({ planOrderUpload: true } as IState);
    }

    onPlanOrderExternalFilesUploadCompleted() {
        this.setState({ planOrderUpload: false } as IState);
    }

    onPlanOrderExternalFileUploadCompleted(response: any): void {
        response = response.xhr.response;

        if (response) {
            let parsedResponse = JSON.parse(response);
            parsedResponse.files.forEach((file) => {
                this.props.actions.addPlanOrderExternalFile(
                    file.id,
                    file.planOrderId,
                    file.fileInfoId,
                    file.filename,
                    file.fileReference,
                    file.fileType,
                    file.thumbnailReference,
                    file.oldFileId,
                    file.category
                );
            });
        }
    }

    deletePlanOrderExternalFile(file: IExternalPlanOrderFile) {
        this.props.actions
            .deletePlanOrderExternalFile(this.props.planOrder.id, file.fileInfoId)
            .catch((error: any) => handleError(error));

        this.setState({ selectedPlanOrderExternalFile: null } as IState);
    }

    uploadStorey2DFile(storeyId: number) {
        let storiesUploads = assign({}, this.state.storiesUploads);

        if (!storiesUploads[storeyId]) {
            storiesUploads[storeyId] = {};
        }

        storiesUploads[storeyId].file2D = true;
        this.setState({ storiesUploads: storiesUploads } as IState);
    }

    onStorey2DFilesUploadCompleted(storeyId: number) {
        let storiesUploads = assign({}, this.state.storiesUploads);
        storiesUploads[storeyId].file2D = false;
        this.setState({ storiesUploads: storiesUploads } as IState);
    }

    onStorey2DFileUploadCompleted(response: any, planOrderStoreyId: number, someFile: any) {
        response = response.xhr.response;
        if (response) {
            response = JSON.parse(response);

            this.props.actions.addPlanOrderStorey2DFile(planOrderStoreyId, response);
        }
    }

    dataURLtoBlob(dataurl): Blob {
        let arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime });
    }

    uploadStorey3DFile(storeyId: number) {
        let storiesUploads = assign({}, this.state.storiesUploads);

        if (!storiesUploads[storeyId]) {
            storiesUploads[storeyId] = {};
        }

        storiesUploads[storeyId].file3D = true;
        this.setState({ storiesUploads: storiesUploads } as IState);
    }

    onStorey3DFilesUploadCompleted(storeyId: number) {
        let storiesUploads = assign({}, this.state.storiesUploads);
        storiesUploads[storeyId].file3D = false;
        this.setState({ storiesUploads: storiesUploads } as IState);
    }

    onStorey3DFileUploadCompleted(response: any, planOrderStoreyId: number, someFile: any) {
        response = response.xhr.response;

        if (response) {
            response = JSON.parse(response);

            this.props.actions.addPlanOrderStorey3DFile(planOrderStoreyId, response);
        }
    }

    approvePlanOrder() {
        this.props.actions
            .approvePlanOrder(this.props.planOrder.id)
            .then(() => {
                notify.success(Translate('planeditor/orders/edit/approve-plan-order-success'));
                this.props.onClose();
            })
            .catch((error: any) => handleError(error));
    }

    generateFiles() {
        this.setState({ isGenerating: true } as IState);
        this.props.actions
            .generateFiles(this.props.planOrder.id)
            .then((result: any) => {
                if (result) {
                    notify.success(Translate('planeditor/orders/edit/generate-files-success'));
                } else {
                    notify.warning(Translate('planeditor/orders/edit/generate-files-nothing-new'));
                }

                this.setState({ isGenerating: false } as IState);
            })
            .catch((error: any) =>
                handleError(error, (errors: any): void => {
                    if (!error) {
                        notify.failure(Translate('planeditor/orders/edit/generate-files-failed'));
                    }

                    this.setState({ isGenerating: false } as IState);
                })
            );
    }

    downloadFiles() {
        this.props.actions
            .downloadFiles(this.props.planOrder.id)
            .catch((error: any) => handleError(error));
    }

    onSelectImage(selectedImage: ISelectedImage, deliveryFileWasSelected: boolean) {
        this.setState({
            showImageModal: true,
            selectedImage: selectedImage,
            deliveryFileWasSelected: deliveryFileWasSelected
        } as IState);
    }

    isFileAnnotation(file: IFile) {
        if (!file) {
            return false;
        }

        return this.props.planOrder.isReturned && file.revisionType === revisionTypes.annotation;
    }

    canAnnotatePlacement() {
        return this.props.planOrder.stories && this.props.planOrder.stories.length > 1;
    }

    showFilePlacementsModal(planOrderStoreyFileId: number) {
        this.setState({
            showFilePlacementsModal: true,
            placementModalPlanOrderStoreyFileId: planOrderStoreyFileId
        } as IState);
    }

    savePlacements(files: any[], planOrderStoreyFileId: number) {
        return this.props.actions
            .updatePlacements(this.props.planOrder.id, files, planOrderStoreyFileId)
            .then((result) => {
                notify.success(Translate('view-order/admin-planorder/update-placements-success'));
                this.setState({
                    showFilePlacementsModal: false,
                    fontSizeRatioForTextEdit: result.fontSizeRatio
                } as IState);
            })
            .catch(() => {
                notify.failure(Translate('view-order/admin-planorder/update-placements-failed'));
            });
    }

    updatePlanEditorConfirmation() {
        if (
            Features.hasFeature(
                Features.PlanEditorOrdersEditUpdatePlanEditorWithoutConfirmation,
                this.props.user
            )
        ) {
            this.updatePlanEditor();
        } else {
            this.setState({ showUpdatePlanEditorConfirmModal: true } as IState);
        }
    }

    updatePlanEditor() {
        this.props.actions
            .updatePlanEditor(
                this.props.planOrder.id,
                this.state.planEditorId,
                Features.hasFeature(
                    Features.PlanEditorOrdersEditUpdatePlanEditorWithoutConfirmation,
                    this.props.user
                )
            )
            .then(() => {
                if (
                    Features.hasFeature(
                        Features.PlanEditorOrdersEditUpdatePlanEditorWithoutConfirmation,
                        this.props.user
                    )
                ) {
                    notify.success(
                        Translate(
                            'planeditor/orders/edit/plan-editor/confirmation/plan-editor-admin/success'
                        )
                    );
                } else {
                    notify.success(
                        Translate('planeditor/orders/edit/plan-editor/confirmation/success')
                    );
                    this.props.onClose();
                }
            })
            .catch(() => {
                notify.failure(Translate('planeditor/orders/edit/plan-editor/confirmation/error'));
            });
    }

    onUploadNewStoreysCompleted() {
        this.setState({ uploadNewStoreys: false } as IState);
    }

    onNewStoreyUploadCompleted(response: any) {
        response = response.xhr.response;

        if (response) {
            response = JSON.parse(response);
            this.props.actions.createStorey(response);
        }
    }

    createEmptyStorey() {
        this.props.actions.createEmptyStorey(this.props.planOrder.id);
    }

    uploadNewStoreys() {
        this.setState({ uploadNewStoreys: true } as IState);
    }

    updateSort(id: number, direction: 'up' | 'down') {
        this.props.actions
            .updateSort(this.props.planOrder.id, {
                id: id,
                direction: direction
            })
            .catch(() => {
                notify.failure(Translate('order/plan/error/sort'));
            });
    }

    updateStorey(
        planOrderStoreyId: number,
        storeyId: number,
        storeyIndex: number,
        customLabel?: string
    ) {
        this.props.actions
            .updateStorey(this.props.planOrder.id, planOrderStoreyId, {
                storeyId: storeyId,
                customStoreyName: customLabel
            })
            .then(() => {
                let errors = assign({}, this.state.errors);
                delete errors[`stories[${storeyIndex}]`];
                this.setState({ errors: errors } as IState);
            })
            .catch(() => {
                notify.failure(Translate('order/plan/error/storey'));
            });
    }

    onChangeStorey(item: any, storeyIndex: number) {
        let value = item != null ? item.value : 0;

        if (value === undefined || value === null) {
            return;
        }

        let label = null;

        if (value === -1 || (item && typeof item.value === String.name.toLocaleLowerCase())) {
            value = -1;
            label = item.label;
        }

        this.updateStorey(this.props.planOrder.stories[storeyIndex].id, value, storeyIndex, label);
    }

    deleteStorey(storeyId: number) {
        this.setState({
            showDeleteStoreyConfirmation: true,
            confirmationDoAction: () => {
                this.props.actions.deleteStorey(this.props.planOrder.id, storeyId).catch(() => {
                    notify.failure(Translate('order/plan/error/delete'));
                });
            }
        } as IState);
    }

    openStoreyExternalFileModal(storeyIndex: number) {
        this.setState({
            showStoreyExternalFilesModal: true,
            selectedExternalStoreyIndex: storeyIndex
        } as IState);
    }

    uploadStoreyFile(storeyId: number, property: string) {
        let storiesUploads = assign({}, this.state.storiesUploads);

        if (!storiesUploads[storeyId]) {
            storiesUploads[storeyId] = {};
        }

        storiesUploads[storeyId][property] = true;
        this.setState({ storiesUploads: storiesUploads } as IState);
    }

    shouldGenerate(): boolean {
        return this.props.planOrder.shouldGenerate;
    }

    getExternalFiles(): IFileInfo[] {
        let internals = this.props.planOrder.internalExternalFiles
            ? this.props.planOrder.internalExternalFiles
            : [];
        let delivers = this.props.planOrder.externalDeliveryFiles
            ? this.props.planOrder.externalDeliveryFiles
            : [];

        let result = internals.concat(delivers) as IFileInfo[];
        return result;
    }

    deleteDeliveryFile(reference: string) {
        this.props.actions
            .deleteDeliveryFile(reference)
            .then(() => {
                this.setState({
                    showImageModal: false,
                    selectedImage: null
                } as IState);
                notify.success(Translate('order/plan/delivery-file/removed-successfully'));
            })
            .catch((error: any) => handleError(error));
    }
}

function mapStateToProps(state: any) {
    return {
        planOrder: state.ordersList.edit.planOrder,
        planEditors: state.ordersList.edit.planEditors,
        annotation: state.ordersList.annotation,
        user: state.user,
        compareStoreys: state.ordersList.edit.compareStoreys
    };
}

function mapDispatchToProps(dispatch: any): { actions: IActions } {
    return {
        actions: {
            editPlanOrder: (planOrderId: number) => dispatch(actions.editPlanOrder(planOrderId)),
            addPlanOrderExternalFile: (
                id: number,
                planOrderId: number,
                fileInfoId: number,
                filename: string,
                fileReference: string,
                fileType: string,
                thumbnailReference: string,
                oldFileId: number,
                category: number
            ) =>
                dispatch(
                    actions.addPlanOrderExternalFile(
                        id,
                        planOrderId,
                        fileInfoId,
                        filename,
                        fileReference,
                        fileType,
                        thumbnailReference,
                        oldFileId,
                        category
                    )
                ),
            addPlanOrderStoreyExternalFile: (
                planOrderStoreyId: number,
                fileInfoId: number,
                filename: string,
                fileReference: string,
                fileType: string,
                thumbnailReference: string,
                oldFileInfoId: number,
                oldFileReference: string
            ) =>
                dispatch(
                    actions.addPlanOrderStoreyExternalFile(
                        planOrderStoreyId,
                        fileInfoId,
                        filename,
                        fileReference,
                        fileType,
                        thumbnailReference,
                        oldFileInfoId,
                        oldFileReference
                    )
                ),
            deletePlanOrderExternalFile: (planOrderId: number, fileInfoId: number) =>
                dispatch(actions.deletePlanOrderExternalFile(planOrderId, fileInfoId)),
            deletePlanOrderStoreyExternalFile: (
                planOrderId: number,
                planOrderStoreyId: number,
                fileInfoId: number
            ) =>
                dispatch(
                    actions.deletePlanOrderStoreyExternalFile(
                        planOrderId,
                        planOrderStoreyId,
                        fileInfoId
                    )
                ),
            addPlanOrderStorey2DFile: (planOrderStoreyId: number, file: IFile) =>
                dispatch(actions.addPlanOrderStorey2DFile(planOrderStoreyId, file)),
            addPlanOrderStorey3DFile: (planOrderStoreyId: number, file: IFile) =>
                dispatch(actions.addPlanOrderStorey3DFile(planOrderStoreyId, file)),
            approvePlanOrder: (planOrderId: number) =>
                dispatch(actions.approvePlanOrder(planOrderId)),
            generateFiles: (planOrderId: number) => dispatch(actions.generateFiles(planOrderId)),
            downloadFiles: (planOrderId: number) => dispatch(actions.downloadFiles(planOrderId)),
            editAnnotation: (id: number) => dispatch(actions.editAnnotation(id, AnnotationTexts)),
            saveAnnotationTexts: (planOrderFileRevisionId: number, data: string) =>
                dispatch(actions.saveAnnotationTexts(planOrderFileRevisionId, data)),
            updatePlacements: (planOrderId: number, files: any[], planOrderStoreyFileId: number) =>
                dispatch(actions.updatePlacements(planOrderId, files, planOrderStoreyFileId)),
            updatePlanEditor: (
                planOrderId: number,
                planEditorId: number,
                dontRemoveFromList: boolean
            ) => dispatch(actions.updatePlanEditor(planOrderId, planEditorId, dontRemoveFromList)),
            createEmptyStorey: (planOrderId: number) =>
                dispatch(actions.createEmptyStorey(planOrderId)),
            createStorey: (storey: any) => dispatch(actions.createStorey(storey)),
            updateSort: (planOrderId: number, storey: IUpdateSortModel) =>
                dispatch(actions.updatePlanOrderStoreySort(planOrderId, storey)),
            deleteStorey: (planOrder: number, storeyId: number) =>
                dispatch(actions.deleteStorey(planOrder, storeyId)),
            updateStorey: (planOrderId: number, planOrderStoreyId: number, data: IUpdateStorey) =>
                dispatch(actions.updatePlanOrder(planOrderId, planOrderStoreyId, data)),
            deleteDeliveryFile: (reference: string) =>
                dispatch(actions.deleteDeliveryFile(reference)),
            getCompareData: (planOrderId: number) => dispatch(actions.getCompareData(planOrderId))
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(PlanEditorOrderModal);
